'use client';

import type { CSSProperties, ComponentPropsWithRef, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '@ncai-axe/react-ui-v3';
import styles from './styles.module.scss';
import { usePopover } from './PopoverContext';

interface PopoverTriggerImplProps extends ComponentPropsWithRef<'button'> {
  id: string;
  children: ReactNode;
  targetId: string;
  className?: string;
  style?: CSSProperties;
}

function PopoverTriggerImpl({
  id,
  children,
  targetId,
  className,
  style,
  ...props
}: PopoverTriggerImplProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const setPopoverTarget = (targetElementId: string) => {
    const target = document.getElementById(targetElementId);
    if (!target || !triggerRef.current) return;
    triggerRef.current.popoverTargetElement = target;
    triggerRef.current.style.setProperty('anchor-name', `--${targetElementId}`);
  };

  useEffect(() => {
    setPopoverTarget(targetId);
  }, [targetId]);

  return (
    <button
      id={id}
      ref={triggerRef}
      type="button"
      className={cn(styles.popoverTrigger, className)}
      style={style}
      {...props}
    >
      {children}
    </button>
  );
}

interface PopoverTriggerProps extends ComponentPropsWithRef<'button'> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  open?: boolean;
}

export default function PopoverTrigger({
  children,
  className,
  style,
  disabled,
  open,
  type = 'button',
  ...props
}: PopoverTriggerProps) {
  const { triggerId, contentId } = usePopover();
  return (
    <PopoverTriggerImpl
      className={className}
      id={triggerId}
      targetId={contentId}
      style={style}
      disabled={disabled}
      data-active={open}
      type={type}
      {...props}
    >
      {children}
    </PopoverTriggerImpl>
  );
}

PopoverTrigger.displayName = 'PopoverTrigger';
